import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPen, faTrash, faCheck } from '@fortawesome/free-solid-svg-icons';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { UserModalComponent } from '../../core/components/user-modal/user-modal.component';
import { SessionService } from '../../core/services/session.service';
import { AdminUsersService, AdminUserPayload } from '../../core/services/admin-users.service';
import { AdminCategoriesService } from '../../core/services/admin-categories.service';

interface AdminUser {
    id: string;
    username: string;
    name?: string;
    mobile?: string;
    email?: string;
    active?: boolean;
    isAdmin?: boolean;
    canAccessOrders?: boolean;
    categoryIds?: string[];
    categoryNames?: string[];
}

@Component({
    selector: 'app-users',
    imports: [CommonModule, FormsModule, FontAwesomeModule, NgbTooltip, MenuBarComponent, FooterComponent, AdminNavComponent],
    templateUrl: './users.component.html',
    styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit {
    users: AdminUser[] = [];
    categories: { id: string; categoryName: string }[] = [];
    loading = false;
    search = '';

    readonly faPen = faPen;
    readonly faTrash = faTrash;
    readonly faCheck = faCheck;

    constructor(
        private usersService: AdminUsersService,
        private categoriesService: AdminCategoriesService,
        private session: SessionService,
        private router: Router,
        private modalService: NgbModal,
        private toastr: ToastrService,
    ) { }

    ngOnInit(): void {
        if (!this.session.isLoggedIn() || !this.session.isAdmin()) {
            this.router.navigate(['/']);
            return;
        }
        this.loadCategories();
        this.load();
    }

    get filteredUsers(): AdminUser[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.users;
        return this.users.filter((u) =>
            (u.username || '').toLowerCase().includes(q) ||
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q),
        );
    }

    loadCategories(): void {
        this.categoriesService.list().subscribe({
            next: (res) => { this.categories = res?.categories ?? []; },
            error: () => { this.categories = []; },
        });
    }

    load(): void {
        this.loading = true;
        this.usersService.list().subscribe({
            next: (res) => {
                this.users = res?.users ?? [];
                this.loading = false;
            },
            error: () => {
                this.users = [];
                this.loading = false;
            },
        });
    }

    openUserModal(user?: AdminUser): void {
        const ref = this.modalService.open(UserModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.categories = this.categories;
        if (user) {
            ref.componentInstance.user = { ...user };
        }
        ref.result.then(
            (data: AdminUser & { id?: string; isAdmin?: boolean; canAccessOrders?: boolean; password?: string }) => {
                if (!data) return;
                const payload: AdminUserPayload = {
                    username: data.username,
                    name: data.name,
                    mobile: data.mobile,
                    email: data.email,
                    active: data.active,
                    isAdmin: data.isAdmin,
                    canAccessOrders: data.canAccessOrders,
                    categoryIds: data.categoryIds,
                    ...(data.password ? { password: data.password } : {}),
                };
                const request$ = data.id
                    ? this.usersService.update(data.id, payload)
                    : this.usersService.create(payload);
                request$.subscribe({
                    next: () => {
                        this.toastr.success(data.id ? 'User updated' : 'User created');
                        this.load();
                    },
                    error: () => this.toastr.error(data.id ? 'Unable to update user' : 'Unable to create user'),
                });
            },
            () => { /* dismissed */ },
        );
    }

    remove(u: AdminUser): void {
        if (!confirm(`Delete user "${u.username}"?`)) return;
        this.usersService.delete(u.id).subscribe({
            next: () => {
                this.toastr.success('User deleted');
                this.load();
            },
            error: () => this.toastr.error('Unable to delete user'),
        });
    }
}
